import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  CheckCircle2, Clock, AlertCircle, ListTodo, Calendar, 
  TrendingUp, TrendingDown, User, Award, Target, CheckSquare, 
  BarChart3, Bell, ChevronRight, Send, Workflow, Megaphone,
  GitBranch, ClipboardList, Factory, Undo2, Package, FileText, ShoppingCart, Wrench, Truck, Users, Quote
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getTaskCompletionBehavior } from '@/hooks/useTaskCompletion';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper functions for score colors (positive scoring - higher is better)
const getScoreColor = (score) => {
  if (score >= 90) return 'text-green-600';
  if (score >= 70) return 'text-yellow-600';
  if (score >= 50) return 'text-orange-600';
  return 'text-red-600';
};

const getScoreBadge = (score) => {
  if (score >= 90) return 'bg-green-100 text-green-800';
  if (score >= 70) return 'bg-yellow-100 text-yellow-800';
  if (score >= 50) return 'bg-orange-100 text-orange-800';
  return 'bg-red-100 text-red-800';
};

// Helper functions for negative scoring (lower is better)
const getNegativeScoreColor = (pct) => {
  if (pct === 0) return 'text-green-600';
  if (pct <= 10) return 'text-green-500';
  if (pct <= 25) return 'text-yellow-600';
  if (pct <= 50) return 'text-orange-600';
  return 'text-red-600';
};

const getNegativeScoreBg = (pct) => {
  if (pct === 0) return 'bg-green-50';
  if (pct <= 10) return 'bg-green-50';
  if (pct <= 25) return 'bg-yellow-50';
  if (pct <= 50) return 'bg-orange-50';
  return 'bg-red-50';
};

// Weightage matrix for MIS scoring
const WEIGHTAGE_MATRIX = {
  production: 30,
  sws: 30,  // Smart Workflow System (replaced FMS)
  delegation: 20,
  checklist: 20
};

// Calculate weighted percentage using weightage matrix
const calculateWeightedPct = (taskMatrix, metric) => {
  // metric is either 'pct_not_done' or 'pct_not_done_on_time'
  const { delegation, checklist, sws } = taskMatrix;
  
  // Get total tasks to determine dynamic weights
  const delegationTasks = delegation?.total_due || 0;
  const checklistTasks = checklist?.total_due || 0;
  const swsTasks = sws?.total_due || 0;
  const totalTasks = delegationTasks + checklistTasks + swsTasks;
  
  if (totalTasks === 0) return 0;
  
  // Calculate weighted average using base weights
  // Only include categories that have tasks
  let totalWeight = 0;
  let weightedSum = 0;
  
  if (delegationTasks > 0) {
    weightedSum += (delegation?.[metric] || 0) * WEIGHTAGE_MATRIX.delegation;
    totalWeight += WEIGHTAGE_MATRIX.delegation;
  }
  if (checklistTasks > 0) {
    weightedSum += (checklist?.[metric] || 0) * WEIGHTAGE_MATRIX.checklist;
    totalWeight += WEIGHTAGE_MATRIX.checklist;
  }
  if (swsTasks > 0) {
    weightedSum += (sws?.[metric] || 0) * WEIGHTAGE_MATRIX.sws;
    totalWeight += WEIGHTAGE_MATRIX.sws;
  }
  
  return totalWeight > 0 ? weightedSum / totalWeight : 0;
};

export default function EmployeeDashboard() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    todayTasks: 0,
    pendingTasks: 0,
    completedTasks: 0,
    upcomingTasks: 0,
    weeklyProgress: 0
  });
  const [todaysTasks, setTodaysTasks] = useState([]);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  
  // Week selection state
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState('');
  
  // MIS Score state
  const [misScoreData, setMisScoreData] = useState(null);
  const [loadingMIS, setLoadingMIS] = useState(true);
  const [overallMisScore, setOverallMisScore] = useState(null); // Average from all previous weeks
  const [overallEscalations, setOverallEscalations] = useState({ delayed: 0, critical: 0, extreme_critical: 0 });
  
  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [todayAnnouncements, setTodayAnnouncements] = useState([]);
  const [celebrations, setCelebrations] = useState([]);
  const [dailyQuote, setDailyQuote] = useState('');
  const [absentees, setAbsentees] = useState({ count: 0, absentees: [] });
  const [showAbsenteeList, setShowAbsenteeList] = useState(false);
  
  // Task counts for dashboard widget
  const [taskCounts, setTaskCounts] = useState({
    sws: 0,
    delegation: 0,
    checklist: 0,
    total: 0
  });
  
  // Task Matrix from MIS API (v2 - negative scoring)
  const [taskMatrix, setTaskMatrix] = useState({
    delegation: { not_done: 0, pct_not_done: 0, pct_not_done_on_time: 0, total_due: 0, escalation: {} },
    checklist: { not_done: 0, pct_not_done: 0, pct_not_done_on_time: 0, total_due: 0, escalation: {} },
    sws: { not_done: 0, pct_not_done: 0, pct_not_done_on_time: 0, total_due: 0, escalation: {} },
    overall: { weighted_score: 0, pct_not_done: 0, pct_not_done_on_time: 0, total_escalations: 0 }
  });

  // Task Details Dialog states (for clickable scorecard headers)
  const [taskDetailsOpen, setTaskDetailsOpen] = useState(false);
  const [taskDetailsCategory, setTaskDetailsCategory] = useState(null);
  const [taskDetailsData, setTaskDetailsData] = useState(null);
  const [taskDetailsLoading, setTaskDetailsLoading] = useState(false);
  const [taskDetailsFilter, setTaskDetailsFilter] = useState('all');

  // Optional widgets data (based on user's allowed_reports)
  const [optionalWidgets, setOptionalWidgets] = useState({
    pending_gate_entries: { data: null, loading: false, error: null },
    pending_jc_issues: { data: null, loading: false, error: null },
    pending_indents: { data: null, loading: false, error: null },
    pending_pos: { data: null, loading: false, error: null },
    incoming_transfers: { data: null, loading: false, error: null }
  });

  // Check if user has permission for a report
  const hasReportPermission = (reportKey) => {
    // Pre-built reports are always shown
    const prebuiltReports = ['todays_celebrations', 'todays_announcements', 'mis_scores', 'action_required'];
    if (prebuiltReports.includes(reportKey)) return true;
    // Check user's allowed_reports
    return user?.allowed_reports?.includes(reportKey) || false;
  };

  // Fetch optional widget data
  const fetchOptionalWidgetData = async (widgetKey, endpoint) => {
    if (!hasReportPermission(widgetKey)) return;
    
    setOptionalWidgets(prev => ({
      ...prev,
      [widgetKey]: { ...prev[widgetKey], loading: true, error: null }
    }));
    
    try {
      const response = await axios.get(`${API_URL}${endpoint}`, { headers: getAuthHeader() });
      setOptionalWidgets(prev => ({
        ...prev,
        [widgetKey]: { data: response.data, loading: false, error: null }
      }));
    } catch (error) {
      console.error(`Failed to fetch ${widgetKey}:`, error);
      setOptionalWidgets(prev => ({
        ...prev,
        [widgetKey]: { data: null, loading: false, error: 'Failed to load' }
      }));
    }
  };

  // Fetch available weeks
  const fetchAvailableWeeks = async () => {
    try {
      const response = await axios.get(`${API_URL}/mis/available-weeks`, { headers: getAuthHeader() });
      setAvailableWeeks(response.data);
      // Set default to current week
      const currentWeek = response.data.find(w => w.is_current);
      if (currentWeek) {
        setSelectedWeek(`${currentWeek.week_number}-${currentWeek.year}`);
      }
    } catch (error) {
      console.error('Failed to fetch weeks:', error);
    }
  };

  // Fetch task details for popup (when clicking on scorecard headers)
  const fetchTaskDetails = useCallback(async (category) => {
    if (!selectedWeek) return;
    
    // Use employee_id from MIS data (matches scorecard data)
    const employeeId = misScoreData?.employee_id;
    if (!employeeId) {
      toast.error('Employee data not available');
      return;
    }
    
    const [weekNum, weekYear] = selectedWeek.split('-').map(Number);
    if (!weekNum || !weekYear) return;

    try {
      setTaskDetailsLoading(true);
      setTaskDetailsCategory(category);
      setTaskDetailsFilter('all');
      setTaskDetailsOpen(true);
      
      const params = { 
        week_number: weekNum, 
        year: weekYear,
        employee_id: employeeId
      };
      
      const response = await axios.get(`${API_URL}/mis/task-details/${category}`, {
        params,
        headers: getAuthHeader()
      });
      setTaskDetailsData(response.data);
    } catch (error) {
      console.error('Error fetching task details:', error);
      toast.error('Failed to load task details');
      setTaskDetailsOpen(false);
    } finally {
      setTaskDetailsLoading(false);
    }
  }, [selectedWeek, misScoreData?.employee_id, getAuthHeader]);

  // Get filtered tasks based on selected filter
  const filteredTaskDetails = useMemo(() => {
    if (!taskDetailsData?.tasks) return [];
    if (taskDetailsFilter === 'all') return taskDetailsData.tasks;
    return taskDetailsData.tasks.filter(t => t.status === taskDetailsFilter);
  }, [taskDetailsData, taskDetailsFilter]);

  // Fetch MIS data for selected week and current user (using v2 API)
  const fetchMISData = useCallback(async () => {
    if (!selectedWeek) return;
    
    setLoadingMIS(true);
    try {
      const [weekNum, weekYear] = selectedWeek.split('-');
      
      // Fetch v2 productivity data (with ND/NDOT metrics)
      const prodResponse = await axios.get(
        `${API_URL}/mis/employee-productivity-v2?week_number=${weekNum}&week_year=${weekYear}`,
        { headers: getAuthHeader() }
      );
      
      // Find current user's data
      const userEmail = user?.email?.toLowerCase();
      const userName = user?.name?.toLowerCase();
      const employeeId = user?.employee_id;
      
      const userTaskData = prodResponse.data?.employees?.find(e => 
        e.employee_id === employeeId ||
        e.name?.toLowerCase() === userName ||
        e.email?.toLowerCase() === userEmail
      );
      
      if (userTaskData) {
        setTaskMatrix({
          delegation: userTaskData.delegation || { not_done: 0, pct_not_done: 0, pct_not_done_on_time: 0, total_due: 0, escalation: {} },
          checklist: userTaskData.checklist || { not_done: 0, pct_not_done: 0, pct_not_done_on_time: 0, total_due: 0, escalation: {} },
          sws: userTaskData.sws || { not_done: 0, pct_not_done: 0, pct_not_done_on_time: 0, total_due: 0, escalation: {} },
          overall: userTaskData.overall || { weighted_score: 0, pct_not_done: 0, pct_not_done_on_time: 0, total_escalations: 0 }
        });
        // Also set MIS score data for the scorecard (include employee_id for task details popup)
        setMisScoreData({
          employee_id: userTaskData.employee_id,
          name: userTaskData.name,
          employee_code: userTaskData.employee_code,
          delegation: userTaskData.delegation,
          checklist: userTaskData.checklist,
          sws: userTaskData.sws,
          overall: userTaskData.overall
        });
      } else {
        setTaskMatrix({
          delegation: { not_done: 0, pct_not_done: 0, pct_not_done_on_time: 0, total_due: 0, escalation: {} },
          checklist: { not_done: 0, pct_not_done: 0, pct_not_done_on_time: 0, total_due: 0, escalation: {} },
          sws: { not_done: 0, pct_not_done: 0, pct_not_done_on_time: 0, total_due: 0, escalation: {} },
          overall: { weighted_score: 0, pct_not_done: 0, pct_not_done_on_time: 0, total_escalations: 0 }
        });
        setMisScoreData(null);
      }
      
    } catch (error) {
      console.error('Error fetching MIS data:', error);
      setMisScoreData(null);
    } finally {
      setLoadingMIS(false);
    }
  }, [selectedWeek, user, getAuthHeader]);

  // Fetch overall MIS score (average of ALL previous weeks) using v2 API
  // Formula: (Avg Weighted % ND × 60%) + (Avg Weighted % NDOT × 40%)
  // Lower score = Better performance (0 = perfect)
  const fetchOverallMISScore = async () => {
    try {
      // First get available weeks from API to determine current week accurately
      const weeksResponse = await axios.get(`${API_URL}/mis/available-weeks`, { headers: getAuthHeader() });
      const availableWeeksList = weeksResponse.data || [];
      const currentWeekData = availableWeeksList.find(w => w.is_current);
      
      if (!currentWeekData) {
        return;
      }
      
      const currentWeek = currentWeekData.week_number;
      const currentYear = currentWeekData.year;
      
      // Fetch ALL previous weeks (excluding current week)
      const weeksToFetch = currentWeek - 1; // All weeks before current
      let totalWeightedND = 0;
      let totalWeightedNDOT = 0;
      let validWeeks = 0;
      let totalDelayed = 0;
      let totalCritical = 0;
      let totalExtremeCritical = 0;
      
      for (let i = 1; i <= weeksToFetch; i++) {
        const weekNum = i;
        try {
          const res = await axios.get(
            `${API_URL}/mis/employee-productivity-v2?week_number=${weekNum}&week_year=${currentYear}`,
            { headers: getAuthHeader() }
          );
          
          const userScore = res.data?.employees?.find(e => 
            e.employee_id === user?.employee_id ||
            e.name?.toLowerCase() === user?.name?.toLowerCase()
          );
          
          if (userScore) {
            // Calculate weighted scores for this week
            const weekMatrix = {
              delegation: userScore.delegation,
              checklist: userScore.checklist,
              sws: userScore.sws
            };
            
            const weightedND = calculateWeightedPct(weekMatrix, 'pct_not_done');
            const weightedNDOT = calculateWeightedPct(weekMatrix, 'pct_not_done_on_time');
            
            // Only count weeks with actual tasks
            const hasTasks = (userScore.delegation?.total_due || 0) + 
                           (userScore.checklist?.total_due || 0) + 
                           (userScore.sws?.total_due || 0) > 0;
            
            if (hasTasks) {
              totalWeightedND += weightedND;
              totalWeightedNDOT += weightedNDOT;
              validWeeks++;
            }
            
            // Accumulate escalations
            totalDelayed += (userScore.delegation?.escalation?.delayed || 0) +
                          (userScore.checklist?.escalation?.delayed || 0) +
                          (userScore.sws?.escalation?.delayed || 0);
            totalCritical += (userScore.delegation?.escalation?.critical || 0) +
                           (userScore.checklist?.escalation?.critical || 0) +
                           (userScore.sws?.escalation?.critical || 0);
            totalExtremeCritical += (userScore.delegation?.escalation?.extreme_critical || 0) +
                                   (userScore.checklist?.escalation?.extreme_critical || 0) +
                                   (userScore.sws?.escalation?.extreme_critical || 0);
          }
        } catch (e) {
          // Week data not available
        }
      }
      
      if (validWeeks > 0) {
        // Calculate averages
        const avgWeightedND = totalWeightedND / validWeeks;
        const avgWeightedNDOT = totalWeightedNDOT / validWeeks;
        
        // Apply weightage: 60% ND + 40% NDOT
        // Lower score = better performance (0 = perfect)
        const overallScore = (avgWeightedND * 0.6) + (avgWeightedNDOT * 0.4);
        setOverallMisScore(overallScore.toFixed(1));
      }
      
      setOverallEscalations({
        delayed: totalDelayed,
        critical: totalCritical,
        extreme_critical: totalExtremeCritical
      });
    } catch (error) {
      console.error('Error fetching overall MIS score:', error);
    }
  };

  useEffect(() => {
    fetchAvailableWeeks();
    fetchDashboardData();
    fetchAnnouncements();
    fetchTodayAnnouncements();
    fetchCelebrations();
    fetchDailyQuote();
    fetchAbsentees();
    fetchTaskCounts();
  }, []);

  // Focus-based refresh: re-fetch tasks when tab becomes visible again.
  // Catches auto-completions that happen in another tab or via a background
  // system event (e.g., PO created in Purchase Orders tab → indent delegation
  // auto-completed → dashboard needs to drop it from "Today's Tasks").
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        fetchDashboardData();
        fetchTaskCounts();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch overall MIS score when user is available
  useEffect(() => {
    if (user) {
      fetchOverallMISScore();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fetch optional widgets based on user permissions
  useEffect(() => {
    if (user && user.allowed_reports) {
      // Fetch data for optional widgets that user has permission for
      if (hasReportPermission('pending_gate_entries')) {
        fetchOptionalWidgetData('pending_gate_entries', '/dashboard/widgets/pending-gate-entries');
      }
      if (hasReportPermission('pending_jc_issues')) {
        fetchOptionalWidgetData('pending_jc_issues', '/dashboard/widgets/pending-jc-issues');
      }
      if (hasReportPermission('pending_indents')) {
        fetchOptionalWidgetData('pending_indents', '/dashboard/widgets/pending-indents');
      }
      if (hasReportPermission('pending_pos')) {
        fetchOptionalWidgetData('pending_pos', '/dashboard/widgets/pending-pos');
      }
    }
    // Always fetch incoming transfers (based on team mapping, not permissions)
    fetchOptionalWidgetData('incoming_transfers', '/dashboard/widgets/incoming-transfers');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (selectedWeek) {
      fetchMISData();
    }
  }, [selectedWeek, fetchMISData]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const delegationRes = await axios.get(`${API_URL}/delegation-tasks`, { headers: getAuthHeader() });
      // Fetch checklist instances WITH completed tasks for "This Week's Completions"
      const checklistRes = await axios.get(`${API_URL}/checklist-instances/my-tasks?include_completed=true`, { headers: getAuthHeader() });
      // Fetch SWS tasks
      const swsRes = await axios.get(`${API_URL}/sws-tasks/my-tasks?status=pending,in_progress,completed`, { headers: getAuthHeader() });

      const today = new Date().toISOString().split('T')[0];
      const userId = user?.id;
      
      const myDelegationTasks = delegationRes.data.filter(t => 
        (t.assigned_to_id === userId || t.assigned_to === userId) &&
        t.status !== 'cancelled'
      );
      
      // Checklist instances are already filtered by the backend
      // Also filter out schedule_invalidated instances (tasks whose schedule was changed)
      const myChecklistTasks = checklistRes.data.filter(t => !t.schedule_invalidated);
      
      // SWS tasks are already filtered by backend
      const mySWSTasks = swsRes.data || [];

      // Helper to check if task is completed (accepts both 'completed' and 'done' statuses)
      const isTaskCompleted = (t) => t.status === 'completed' || t.status === 'done';

      const pendingDelegation = myDelegationTasks.filter(t => !isTaskCompleted(t)).length;
      const completedDelegation = myDelegationTasks.filter(t => isTaskCompleted(t)).length;
      const pendingChecklists = myChecklistTasks.filter(t => !isTaskCompleted(t)).length;
      const completedChecklists = myChecklistTasks.filter(t => isTaskCompleted(t)).length;

      // Overdue tasks (due date < today and not completed)
      const overdueDelegation = myDelegationTasks.filter(t => {
        if (isTaskCompleted(t)) return false;
        const dueDate = t.due_date || t.deadline;
        if (!dueDate) return false;
        return dueDate.split('T')[0] < today;
      });

      const overdueChecklists = myChecklistTasks.filter(t => {
        if (isTaskCompleted(t)) return false;
        // Use occurrence_date for instances, fallback to due_date
        const dueDate = t.occurrence_date || t.due_date || t.next_due;
        if (!dueDate) return false;
        return dueDate.split('T')[0] < today;
      });

      // SWS overdue tasks (due_at < now)
      const overdueSWS = mySWSTasks.filter(t => {
        if (isTaskCompleted(t)) return false;
        const dueAt = t.due_at;
        if (!dueAt) return false;
        return dueAt.split('T')[0] < today;
      });

      // Today's tasks (due date = today and not completed)
      const todayDelegation = myDelegationTasks.filter(t => {
        if (isTaskCompleted(t)) return false;
        const dueDate = t.due_date || t.deadline;
        if (!dueDate) return false;
        return dueDate.split('T')[0] === today;
      });

      const todayChecklists = myChecklistTasks.filter(t => {
        if (isTaskCompleted(t)) return false;
        // Use occurrence_date for instances
        const dueDate = t.occurrence_date || t.due_date || t.next_due;
        if (!dueDate) return false;
        return dueDate.split('T')[0] === today;
      });

      // SWS today's tasks (due_at is today)
      const todaySWS = mySWSTasks.filter(t => {
        if (isTaskCompleted(t)) return false;
        const dueAt = t.due_at;
        if (!dueAt) return false;
        return dueAt.split('T')[0] === today;
      });

      // Pending SWS tasks (for cases where due_at is not set, show all pending SWS as today's tasks)
      const pendingSWSNoDate = mySWSTasks.filter(t => {
        if (isTaskCompleted(t)) return false;
        return !t.due_at; // No due date set
      });

      const totalPending = pendingDelegation + pendingChecklists;
      const completedSWS = mySWSTasks.filter(t => isTaskCompleted(t)).length;
      const completedTotal = completedDelegation + completedChecklists + completedSWS;
      const totalTasks = totalPending + completedTotal;
      const progress = totalTasks > 0 ? Math.round((completedTotal / totalTasks) * 100) : 0;

      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];

      const upcomingDelegation = myDelegationTasks.filter(t => {
        if (isTaskCompleted(t)) return false;
        const dueDate = t.due_date || t.deadline;
        if (!dueDate) return false;
        const dueDateStr = dueDate.split('T')[0];
        return dueDateStr >= today && dueDateStr <= tomorrowStr;
      }).length;

      const upcomingChecklists = myChecklistTasks.filter(t => {
        if (isTaskCompleted(t)) return false;
        // Use occurrence_date for instances
        const dueDate = t.occurrence_date || t.due_date || t.next_due;
        if (!dueDate) return false;
        const dueDateStr = dueDate.split('T')[0];
        return dueDateStr >= today && dueDateStr <= tomorrowStr;
      }).length;

      setStats({
        todayTasks: todayDelegation.length + todayChecklists.length + todaySWS.length + pendingSWSNoDate.length,
        pendingTasks: totalPending,
        completedTasks: completedTotal,
        upcomingTasks: upcomingDelegation + upcomingChecklists,
        weeklyProgress: progress
      });

      // Combine and sort overdue tasks (oldest first, then by priority)
      const allOverdueTasks = [
        ...overdueDelegation.map(t => ({ ...t, type: 'delegation' })),
        ...overdueChecklists.map(t => ({ ...t, type: 'checklist' })),
        ...overdueSWS.map(t => ({ ...t, type: 'sws', _type: 'sws' }))
      ].sort((a, b) => {
        // Use occurrence_date for checklist instances, due_at for SWS
        const dateA = new Date(a.due_at || a.occurrence_date || a.due_date || a.deadline || a.next_due);
        const dateB = new Date(b.due_at || b.occurrence_date || b.due_date || b.deadline || b.next_due);
        
        // Sort by date (oldest first for overdue)
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA - dateB;
        }
        
        // Then sort by priority (IMP/critical first)
        const priorityOrder = { 'IMP': 0, 'critical': 0, 'high': 1, 'medium': 2, 'low': 3, 'normal': 3 };
        const priorityA = priorityOrder[(a.priority || 'normal').toLowerCase()] ?? 4;
        const priorityB = priorityOrder[(b.priority || 'normal').toLowerCase()] ?? 4;
        return priorityA - priorityB;
      });
      
      setOverdueTasks(allOverdueTasks);

      // Combine and sort today's tasks (include SWS tasks with no due date as "today")
      const allTodayTasks = [
        ...todayDelegation.map(t => ({ ...t, type: 'delegation' })),
        ...todayChecklists.map(t => ({ ...t, type: 'checklist' })),
        ...todaySWS.map(t => ({ ...t, type: 'sws', _type: 'sws' })),
        ...pendingSWSNoDate.map(t => ({ ...t, type: 'sws', _type: 'sws' }))
      ]
      // Sort by priority (IMP/critical first)
      .sort((a, b) => {
        const priorityOrder = { 'IMP': 0, 'critical': 0, 'high': 1, 'medium': 2, 'low': 3, 'normal': 3 };
        const priorityA = priorityOrder[(a.priority || 'normal')] ?? 4;
        const priorityB = priorityOrder[(b.priority || 'normal')] ?? 4;
        return priorityA - priorityB;
      });
      
      setTodaysTasks(allTodayTasks);

      // Calculate current week bounds independently (Monday to Sunday)
      // This ensures we don't depend on availableWeeks being loaded first
      const now = new Date();
      const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Days since Monday
      
      const weekStartDate = new Date(now);
      weekStartDate.setDate(now.getDate() - daysToMonday);
      weekStartDate.setHours(0, 0, 0, 0);
      
      const weekEndDate = new Date(weekStartDate);
      weekEndDate.setDate(weekStartDate.getDate() + 6);
      weekEndDate.setHours(23, 59, 59, 999);

      // Filter tasks completed in current week and sort by completed_at (latest first)
      // Accept both 'completed' and 'done' statuses
      const thisWeekCompleted = [
        ...myDelegationTasks.filter(t => {
          if ((t.status !== 'completed' && t.status !== 'done') || !t.completed_at) return false;
          const completedDate = new Date(t.completed_at);
          return completedDate >= weekStartDate && completedDate <= weekEndDate;
        }).map(t => ({ ...t, type: 'delegation' })),
        ...myChecklistTasks.filter(t => {
          if ((t.status !== 'completed' && t.status !== 'done') || !t.completed_at) return false;
          const completedDate = new Date(t.completed_at);
          return completedDate >= weekStartDate && completedDate <= weekEndDate;
        }).map(t => ({ ...t, type: 'checklist' })),
        ...mySWSTasks.filter(t => {
          if (!isTaskCompleted(t) || !t.completed_at) return false;
          const completedDate = new Date(t.completed_at);
          return completedDate >= weekStartDate && completedDate <= weekEndDate;
        }).map(t => ({ ...t, type: 'sws' }))
      ].sort((a, b) => {
        // Sort by completed_at descending (latest first)
        const dateA = new Date(a.completed_at);
        const dateB = new Date(b.completed_at);
        return dateB - dateA;
      });
      
      setRecentActivity(thisWeekCompleted);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API_URL}/announcements/unacknowledged`, { headers: getAuthHeader() });
      setAnnouncements(res.data || []);
    } catch (error) {
      try {
        const res = await axios.get(`${API_URL}/announcements`, { headers: getAuthHeader() });
        const userAnnouncements = res.data.filter(a => !a.acknowledged);
        setAnnouncements(userAnnouncements);
      } catch {
        console.log('Announcements not available yet');
      }
    }
  };

  // Fetch TODAY's announcements only (including acknowledged ones)
  const fetchTodayAnnouncements = async () => {
    try {
      const res = await axios.get(`${API_URL}/announcements`, { headers: getAuthHeader() });
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      
      // Filter for announcements created TODAY only
      const todayAnns = res.data.filter(a => {
        if (!a.created_at) return false;
        const annDate = a.created_at.split('T')[0];
        // Check is_active (not status)
        return annDate === today && a.is_active !== false;
      });
      
      // Sort by created_at descending
      const sortedAnns = todayAnns.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      setTodayAnnouncements(sortedAnns);
    } catch (error) {
      console.log('Could not fetch today announcements');
    }
  };

  const fetchCelebrations = async () => {
    try {
      const res = await axios.get(`${API_URL}/announcements/celebrations`, { headers: getAuthHeader() });
      setCelebrations(res.data || []);
    } catch (error) {
      console.log('Celebrations endpoint not available');
    }
  };

  const fetchDailyQuote = async () => {
    try {
      const res = await axios.get(`${API_URL}/employee-requests/quote-of-the-day`, { headers: getAuthHeader() });
      setDailyQuote(res.data?.quote || '');
    } catch (error) {
      console.log('Quote endpoint not available');
    }
  };

  const fetchAbsentees = async () => {
    try {
      const res = await axios.get(`${API_URL}/employee-requests/todays-absentees`, { headers: getAuthHeader() });
      setAbsentees(res.data || { count: 0, absentees: [] });
    } catch (error) {
      console.log('Absentees endpoint not available');
    }
  };

  const fetchTaskCounts = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const now = new Date(); // Full datetime for SWS comparison
      
      // Fetch SWS tasks (compare full datetime - hour-based TAT)
      const swsRes = await axios.get(`${API_URL}/sws-tasks/my-tasks?status=pending,in_progress`, { headers: getAuthHeader() });
      const swsDueCount = (swsRes.data || []).filter(t => {
        const dueDateTime = t.due_date || t.deadline;
        if (!dueDateTime) return false;
        return new Date(dueDateTime) <= now; // Full datetime comparison
      }).length;
      
      // Fetch delegation tasks (due today or overdue - date only)
      const delegationRes = await axios.get(`${API_URL}/delegation-tasks`, { headers: getAuthHeader() });
      const myDelegation = delegationRes.data.filter(t => 
        (t.assigned_to_id === user?.id || t.assigned_to === user?.id) &&
        t.status !== 'cancelled'
      );
      const delegationDueCount = myDelegation.filter(t => {
        if (t.status === 'completed' || t.status === 'done') return false;
        const dueDate = t.due_date || t.deadline;
        if (!dueDate) return false;
        const dueDateOnly = dueDate.split('T')[0];
        return dueDateOnly <= today; // Date-only comparison
      }).length;
      
      // Fetch checklist instances (due today or overdue - date only)
      const checklistRes = await axios.get(`${API_URL}/checklist-instances/my-tasks`, { headers: getAuthHeader() });
      const checklistDueCount = checklistRes.data.filter(t => {
        if (t.status === 'completed' || t.status === 'done') return false;
        if (t.schedule_invalidated) return false; // Exclude invalidated instances
        const dueDate = t.due_date || t.next_due;
        if (!dueDate) return false;
        return dueDate.split('T')[0] <= today; // Date-only comparison
      }).length;
      
      setTaskCounts({
        sws: swsDueCount,
        delegation: delegationDueCount,
        checklist: checklistDueCount,
        total: swsDueCount + delegationDueCount + checklistDueCount
      });
    } catch (error) {
      console.log('Could not fetch task counts');
    }
  };

  const acknowledgeAnnouncement = async (announcementId) => {
    try {
      await axios.post(
        `${API_URL}/announcements/acknowledge`,
        { announcement_id: announcementId },
        { headers: getAuthHeader() }
      );
      setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
      toast.success('Announcement acknowledged');
    } catch (error) {
      toast.error('Failed to acknowledge');
    }
  };

  // Mark task as done from Today's Tasks section
  const markTaskDone = async (task) => {
    // Canonical guard — backend sets completion_mode on every task
    if (task?.completion_mode === 'system-action') {
      const msg = task.auto_complete_action
        ? `This task auto-completes when ${task.auto_complete_action}. Manual completion is disabled.`
        : 'This task auto-completes via the system.';
      toast.error(msg);
      return;
    }
    try {
      if (task.type === 'delegation') {
        await axios.patch(
          `${API_URL}/delegation-tasks/${task.id}`,
          { status: 'completed' },
          { headers: getAuthHeader() }
        );
      } else if (task.type === 'checklist') {
        // Use checklist-instances endpoint for instance tasks
        await axios.patch(
          `${API_URL}/checklist-instances/${task.id}/complete`,
          {},
          { headers: getAuthHeader() }
        );
      } else if (task.type === 'sws') {
        // Manual SWS completion (auto-complete SWS tasks are blocked above)
        await axios.post(
          `${API_URL}/sws-tasks/${task.id}/complete`,
          { remarks: null },
          { headers: getAuthHeader() }
        );
      }
      toast.success('Task marked as done!');
      // Refresh data
      fetchDashboardData();
      if (selectedWeek) fetchMISData();
    } catch (error) {
      console.error('Error marking task done:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark task as done');
    }
  };

  // Undo task completion (today only - as per business logic)
  const undoTaskCompletion = async (task) => {
    try {
      const taskType = task.type || (task.occurrence_date || task.task_name ? 'checklist' : 'delegation');
      
      // Use the correct endpoint based on task type
      let endpoint;
      if (taskType === 'checklist') {
        endpoint = `${API_URL}/checklist-instances/${task.id}/undo`;
      } else {
        endpoint = `${API_URL}/delegation-tasks/${task.id}/undo`;
      }
      
      await axios.patch(endpoint, {}, { headers: getAuthHeader() });
      toast.success('Task marked as pending again');
      // Refresh data
      fetchDashboardData();
      if (selectedWeek) fetchMISData();
    } catch (error) {
      console.error('Error undoing task:', error);
      const message = error.response?.data?.detail || 'Failed to undo task';
      toast.error(message);
    }
  };

  // Check if task was completed TODAY (for Undo button eligibility)
  // Business Rule: Undo button only available for tasks marked Done TODAY, until midnight
  // The task itself may be today's or an older delayed task - what matters is WHEN it was marked done
  const isCompletedToday = (task) => {
    if (!task.completed_at) return false;
    const today = new Date().toISOString().split('T')[0];
    const completedDate = task.completed_at.split('T')[0];
    return today === completedDate;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getPriorityColor = (priority) => {
    switch(priority?.toLowerCase()) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-green-100 text-green-800';
    }
  };

  // Get selected week label
  const getSelectedWeekLabel = () => {
    if (!selectedWeek) return '';
    const week = availableWeeks.find(w => `${w.week_number}-${w.year}` === selectedWeek);
    return week ? `${week.start_date} - ${week.end_date}` : '';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6" data-testid="employee-dashboard">
        {/* Welcome Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <User className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{getGreeting()}, {user?.name?.split(' ')[0] || 'Team Member'}!</h1>
              <p className="text-blue-100 mt-1">Here&apos;s your personal dashboard for today</p>
            </div>
          </div>
        </div>

        {/* Today's Celebrations + Announcements Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Today's Celebrations */}
          <Card className="bg-gradient-to-r from-pink-50 to-purple-50 border-pink-200" data-testid="celebrations-banner">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">🎉</span>
                <div className="flex-1">
                  <h3 className="font-semibold text-purple-800">Today&apos;s Celebrations</h3>
                  {celebrations.length === 0 ? (
                    <p className="text-sm text-purple-600 mt-2 italic">No celebrations today</p>
                  ) : (
                    <div className="mt-2 space-y-2">
                      {celebrations.map((c, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <span className="text-lg">{c.icon}</span>
                          <span className="font-medium text-purple-900">{c.name}</span>
                          {c.department && <span className="text-purple-600">({c.department})</span>}
                          <span className="text-purple-700">- {c.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Today's Announcements */}
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 overflow-hidden" data-testid="today-announcements-window">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Megaphone className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div className="flex-1 min-w-0 overflow-hidden">
                  <h3 className="font-semibold text-amber-800">Today&apos;s Announcements</h3>
                  {dailyQuote && (
                    <div className="mt-2 mb-2 p-3 bg-white/60 rounded-lg border border-amber-200/50">
                      <p className="text-base md:text-lg italic text-amber-900/90 font-medium leading-relaxed">&ldquo;{dailyQuote}&rdquo;</p>
                    </div>
                  )}
                  {todayAnnouncements.length === 0 && !dailyQuote ? (
                    <p className="text-sm text-amber-600 mt-2 italic">No announcements for today</p>
                  ) : todayAnnouncements.length === 0 ? null : (
                    <div className="mt-2 space-y-2 max-h-28 overflow-y-auto pr-1">
                      {todayAnnouncements.map((ann, idx) => (
                        <div key={ann.id || idx} className="flex items-start gap-2 pr-1">
                          <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${ann.priority === 'urgent' ? 'bg-red-500' : 'bg-amber-500'}`} />
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <p className="text-sm font-medium text-amber-900 truncate">{ann.title}</p>
                            <p className="text-xs text-amber-700 line-clamp-2 break-words">{ann.message}</p>
                          </div>
                          {ann.priority === 'urgent' && (
                            <Badge variant="destructive" className="text-xs flex-shrink-0">!</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Today's Absentees - Small inline badge */}
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <button className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors cursor-pointer border border-gray-200">
                <Users className="w-3.5 h-3.5" />
                Today&apos;s Absentees: {absentees.count}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-3" align="start">
              <p className="text-sm font-semibold mb-2">Today&apos;s Absentees</p>
              {absentees.count === 0 ? (
                <p className="text-xs text-muted-foreground italic">Everyone is present today!</p>
              ) : (
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {absentees.absentees.map((a, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-gray-100 last:border-0">
                      <span className="font-medium">{a.name}</span>
                      <span className="text-muted-foreground">{a.type}</span>
                    </div>
                  ))}
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>

        {/* Task Counts Widget - Clickable */}
        <Card 
          className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/my-tasks')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <ListTodo className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900">Tasks Due Now</h3>
                  <p className="text-xs text-blue-600">Click to view all tasks</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-center">
                <div className="px-3">
                  <p className="text-2xl font-bold text-blue-900">{taskCounts.total}</p>
                  <p className="text-xs text-blue-600">Total Due</p>
                </div>
                <div className="h-12 w-px bg-blue-200"></div>
                <div className="px-3">
                  <p className="text-lg font-semibold text-orange-700">{taskCounts.sws}</p>
                  <p className="text-xs text-orange-600">SWS</p>
                </div>
                <div className="h-12 w-px bg-blue-200"></div>
                <div className="px-3">
                  <p className="text-lg font-semibold text-green-700">{taskCounts.delegation}</p>
                  <p className="text-xs text-green-600">Delegation</p>
                </div>
                <div className="h-12 w-px bg-blue-200"></div>
                <div className="px-3">
                  <p className="text-lg font-semibold text-purple-700">{taskCounts.checklist}</p>
                  <p className="text-xs text-purple-600">Checklist</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Announcements Banner (if any unacknowledged) */}
        {announcements.length > 0 && (
          <div className="space-y-3" data-testid="announcements-banner">
            {announcements.map(a => (
              <Card 
                key={a.id} 
                className={`${a.priority === 'urgent' ? 'bg-red-50 border-red-300 border-2' : 'bg-amber-50 border-amber-200'}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {a.priority === 'urgent' ? (
                      <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    ) : (
                      <Bell className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold ${a.priority === 'urgent' ? 'text-red-800' : 'text-amber-800'}`}>
                          {a.priority === 'urgent' ? 'Urgent Announcement' : 'Announcement'}
                        </h3>
                        {a.priority === 'urgent' && (
                          <Badge variant="destructive" className="text-xs">URGENT</Badge>
                        )}
                      </div>
                      <div className="mt-2">
                        <p className={`font-medium ${a.priority === 'urgent' ? 'text-red-900' : 'text-amber-900'}`}>{a.title}</p>
                        <p className={`text-sm mt-1 ${a.priority === 'urgent' ? 'text-red-700' : 'text-amber-700'}`}>{a.message}</p>
                        <div className="flex items-center justify-between mt-3">
                          <p className={`text-xs ${a.priority === 'urgent' ? 'text-red-600' : 'text-amber-600'}`}>
                            From: {a.created_by_name}
                          </p>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className={`${a.priority === 'urgent' ? 'border-red-400 text-red-700 hover:bg-red-100' : 'border-amber-400 text-amber-700 hover:bg-amber-100'}`}
                            onClick={() => acknowledgeAnnouncement(a.id)}
                            data-testid={`acknowledge-btn-${a.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Acknowledge
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Week Selector */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                <span className="font-medium">Select Week:</span>
              </div>
              <Select value={selectedWeek} onValueChange={setSelectedWeek}>
                <SelectTrigger className="w-64" data-testid="week-selector">
                  <SelectValue placeholder="Select week" />
                </SelectTrigger>
                <SelectContent>
                  {availableWeeks.map(w => (
                    <SelectItem key={`${w.week_number}-${w.year}`} value={`${w.week_number}-${w.year}`}>
                      {w.label} ({w.start_date} - {w.end_date})
                      {w.is_current && <Badge className="ml-2 text-xs">Current</Badge>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedWeek && (
                <Badge variant="outline" className="text-sm">
                  {getSelectedWeekLabel()}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary Numbers Table - Employee Specific (Same as Admin) */}
        <Card data-testid="summary-numbers-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Summary Numbers - Week {selectedWeek?.split('-')[0] || '-'}
            </CardTitle>
            <CardDescription>Your task performance breakdown for the selected week</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-[180px]">Category</TableHead>
                    <TableHead className="text-center w-[100px]">Total Due</TableHead>
                    <TableHead className="text-center w-[120px]">Due This Week</TableHead>
                    <TableHead className="text-center w-[120px]">Due Until Now</TableHead>
                    <TableHead className="text-center w-[80px]">ND</TableHead>
                    <TableHead className="text-center w-[80px]">NDOT</TableHead>
                    <TableHead className="text-center w-[80px]">ESC</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const categories = [
                      { key: 'delegation', label: 'Delegation', icon: <Send className="w-4 h-4 text-purple-600" />, color: 'text-purple-800' },
                      { key: 'checklist', label: 'Checklist', icon: <ClipboardList className="w-4 h-4 text-green-600" />, color: 'text-green-800' },
                      { key: 'sws', label: 'SWS', icon: <GitBranch className="w-4 h-4 text-orange-600" />, color: 'text-orange-800' },
                      { key: 'production', label: 'Production/Sale', icon: <Factory className="w-4 h-4 text-blue-600" />, color: 'text-blue-800' }
                    ];
                    
                    let totalDue = 0, totalDueThisWeek = 0, totalDueUntilNow = 0, totalND = 0, totalNDOT = 0, totalESC = 0;
                    
                    const rows = categories.map(cat => {
                      const catData = taskMatrix[cat.key] || {};
                      const due = catData.total_due || 0;
                      const dueThisWeek = catData.due_this_week || catData.total_due || 0;
                      const dueUntilNow = catData.due_until_now || catData.total_due || 0;
                      const nd = catData.not_done || 0;
                      const ndot = catData.not_done_on_time || 0;
                      const esc = (catData.escalation?.delayed || 0) + (catData.escalation?.critical || 0) + (catData.escalation?.extreme_critical || 0);
                      
                      totalDue += due;
                      totalDueThisWeek += dueThisWeek;
                      totalDueUntilNow += dueUntilNow;
                      totalND += nd;
                      totalNDOT += ndot;
                      totalESC += esc;
                      
                      return (
                        <TableRow key={cat.key}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {cat.icon}
                              <span className={`font-medium ${cat.color}`}>{cat.label}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">{due}</TableCell>
                          <TableCell className="text-center">{dueThisWeek}</TableCell>
                          <TableCell className="text-center">{dueUntilNow}</TableCell>
                          <TableCell className={`text-center font-bold ${nd > 0 ? 'text-red-600' : 'text-green-600'}`}>{nd}</TableCell>
                          <TableCell className={`text-center font-bold ${ndot > 0 ? 'text-orange-600' : 'text-green-600'}`}>{ndot}</TableCell>
                          <TableCell className={`text-center ${esc > 0 ? 'text-red-600' : ''}`}>{esc || '-'}</TableCell>
                        </TableRow>
                      );
                    });
                    
                    return (
                      <>
                        {rows}
                        <TableRow className="bg-muted/30 font-bold">
                          <TableCell>
                            <span className="font-bold">Total</span>
                          </TableCell>
                          <TableCell className="text-center">{totalDue}</TableCell>
                          <TableCell className="text-center">{totalDueThisWeek}</TableCell>
                          <TableCell className="text-center">{totalDueUntilNow}</TableCell>
                          <TableCell className={`text-center ${totalND > 0 ? 'text-red-600' : 'text-green-600'}`}>{totalND}</TableCell>
                          <TableCell className={`text-center ${totalNDOT > 0 ? 'text-orange-600' : 'text-green-600'}`}>{totalNDOT}</TableCell>
                          <TableCell className={`text-center ${totalESC > 0 ? 'text-red-600' : ''}`}>{totalESC || '-'}</TableCell>
                        </TableRow>
                      </>
                    );
                  })()}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Weighted Productivity Scorecard - Employee Specific (Same as Admin) */}
        <Card className="border-2 border-indigo-200" data-testid="weighted-scorecard">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-indigo-600" />
                  Weighted Productivity Scorecard
                </CardTitle>
                <CardDescription>
                  Performance in % (ND / NDOT) - Lower is better
                </CardDescription>
              </div>
              <Badge className={`text-sm px-3 py-1 ${
                calculateWeightedPct(taskMatrix, 'pct_not_done') === 0 ? 'bg-green-100 text-green-800' :
                calculateWeightedPct(taskMatrix, 'pct_not_done') <= 10 ? 'bg-green-100 text-green-800' :
                calculateWeightedPct(taskMatrix, 'pct_not_done') <= 25 ? 'bg-yellow-100 text-yellow-800' :
                calculateWeightedPct(taskMatrix, 'pct_not_done') <= 40 ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'
              }`}>
                {calculateWeightedPct(taskMatrix, 'pct_not_done') === 0 ? 'Excellent' :
                 calculateWeightedPct(taskMatrix, 'pct_not_done') <= 10 ? 'Excellent' :
                 calculateWeightedPct(taskMatrix, 'pct_not_done') <= 25 ? 'Good' :
                 calculateWeightedPct(taskMatrix, 'pct_not_done') <= 40 ? 'Needs Attention' : 'Critical'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead 
                      className="text-center cursor-pointer hover:bg-purple-50 transition-colors"
                      onClick={() => taskMatrix.delegation?.total_due > 0 && fetchTaskDetails('delegation')}
                    >
                      <div className="flex flex-col items-center">
                        <span>Delegation</span>
                        <span className="text-xs font-normal text-purple-600">% ND / NDOT</span>
                        {taskMatrix.delegation?.total_due > 0 && (
                          <span className="text-xs text-muted-foreground">Click to view</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-center cursor-pointer hover:bg-green-50 transition-colors"
                      onClick={() => taskMatrix.checklist?.total_due > 0 && fetchTaskDetails('checklist')}
                    >
                      <div className="flex flex-col items-center">
                        <span>Checklist</span>
                        <span className="text-xs font-normal text-green-600">% ND / NDOT</span>
                        {taskMatrix.checklist?.total_due > 0 && (
                          <span className="text-xs text-muted-foreground">Click to view</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-center cursor-pointer hover:bg-orange-50 transition-colors"
                      onClick={() => taskMatrix.sws?.total_due > 0 && fetchTaskDetails('sws')}
                    >
                      <div className="flex flex-col items-center">
                        <span>SWS</span>
                        <span className="text-xs font-normal text-orange-600">% ND / NDOT</span>
                        {taskMatrix.sws?.total_due > 0 && (
                          <span className="text-xs text-muted-foreground">Click to view</span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex flex-col items-center">
                        <span>Prod/Sales</span>
                        <span className="text-xs font-normal text-blue-600">% ND / NDOT</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center bg-indigo-50">
                      <div className="flex flex-col items-center">
                        <span className="font-semibold">Weekly</span>
                        <span className="text-xs font-normal text-indigo-600">Weighted Score</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center bg-purple-50">
                      <div className="flex flex-col items-center">
                        <span className="font-semibold">Overall</span>
                        <span className="text-xs font-normal text-purple-600">Weighted Score</span>
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    {/* Delegation */}
                    <TableCell className="text-center">
                      {taskMatrix.delegation?.total_due > 0 ? (
                        <div className="space-y-1">
                          <div className="flex justify-center gap-1 items-center">
                            <span className={`font-bold ${getNegativeScoreColor(taskMatrix.delegation?.pct_not_done || 0)}`}>
                              {(taskMatrix.delegation?.pct_not_done || 0).toFixed(0)}%
                            </span>
                            <span className="text-gray-400">/</span>
                            <span className={`text-sm ${getNegativeScoreColor(taskMatrix.delegation?.pct_not_done_on_time || 0)}`}>
                              {(taskMatrix.delegation?.pct_not_done_on_time || 0).toFixed(0)}%
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {taskMatrix.delegation?.total_due || 0} due
                          </p>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    {/* Checklist */}
                    <TableCell className="text-center">
                      {taskMatrix.checklist?.total_due > 0 ? (
                        <div className="space-y-1">
                          <div className="flex justify-center gap-1 items-center">
                            <span className={`font-bold ${getNegativeScoreColor(taskMatrix.checklist?.pct_not_done || 0)}`}>
                              {(taskMatrix.checklist?.pct_not_done || 0).toFixed(0)}%
                            </span>
                            <span className="text-gray-400">/</span>
                            <span className={`text-sm ${getNegativeScoreColor(taskMatrix.checklist?.pct_not_done_on_time || 0)}`}>
                              {(taskMatrix.checklist?.pct_not_done_on_time || 0).toFixed(0)}%
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {taskMatrix.checklist?.total_due || 0} due
                          </p>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    {/* SWS */}
                    <TableCell className="text-center">
                      {taskMatrix.sws?.total_due > 0 ? (
                        <div className="space-y-1">
                          <div className="flex justify-center gap-1 items-center">
                            <span className={`font-bold ${getNegativeScoreColor(taskMatrix.sws?.pct_not_done || 0)}`}>
                              {(taskMatrix.sws?.pct_not_done || 0).toFixed(0)}%
                            </span>
                            <span className="text-gray-400">/</span>
                            <span className={`text-sm ${getNegativeScoreColor(taskMatrix.sws?.pct_not_done_on_time || 0)}`}>
                              {(taskMatrix.sws?.pct_not_done_on_time || 0).toFixed(0)}%
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {taskMatrix.sws?.total_due || 0} due
                          </p>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    {/* Prod/Sales - N/A for office staff */}
                    <TableCell className="text-center">
                      {taskMatrix.production?.total_due > 0 ? (
                        <div className="space-y-1">
                          <div className="flex justify-center gap-1 items-center">
                            <span className={`font-bold ${getNegativeScoreColor(taskMatrix.production?.pct_not_done || 0)}`}>
                              {(taskMatrix.production?.pct_not_done || 0).toFixed(0)}%
                            </span>
                            <span className="text-gray-400">/</span>
                            <span className={`text-sm ${getNegativeScoreColor(taskMatrix.production?.pct_not_done_on_time || 0)}`}>
                              {(taskMatrix.production?.pct_not_done_on_time || 0).toFixed(0)}%
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {taskMatrix.production?.total_due || 0} due
                          </p>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    {/* Weekly Score */}
                    <TableCell className="text-center bg-indigo-50/50">
                      <div className="space-y-1">
                        <div className={`text-xl font-bold ${getNegativeScoreColor(calculateWeightedPct(taskMatrix, 'pct_not_done'))}`}>
                          {calculateWeightedPct(taskMatrix, 'pct_not_done').toFixed(0)}%
                        </div>
                        <p className="text-xs text-muted-foreground">This Week</p>
                      </div>
                    </TableCell>
                    {/* Overall Score */}
                    <TableCell className="text-center bg-purple-50/50">
                      <div className="space-y-1">
                        <div className={`text-xl font-bold ${overallMisScore ? getNegativeScoreColor(parseFloat(overallMisScore)) : 'text-gray-400'}`}>
                          {overallMisScore ? `${overallMisScore}%` : '-'}
                        </div>
                        <p className="text-xs text-muted-foreground">Cumulative</p>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            
            {/* Algorithm Explanation */}
            <div className="text-xs text-muted-foreground p-3 bg-muted/20 rounded-lg mt-3">
              <p className="font-medium mb-1">Weighted Score Formula:</p>
              <p>(Weighted % ND × 60%) + (Weighted % NDOT × 40%) = Weighted Score</p>
              <p className="mt-1">Lower score = Better performance (0% is perfect)</p>
            </div>
            
            {/* Escalation Summary */}
            <div className="flex items-center gap-4 mt-3 text-sm">
              <span className="font-medium">Escalations:</span>
              <span className="text-yellow-600">{overallEscalations.delayed} Delayed</span>
              <span className="text-orange-600">{overallEscalations.critical} Critical</span>
              <span className="text-red-600">{overallEscalations.extreme_critical} Extreme</span>
            </div>
          </CardContent>
        </Card>

        {/* Negative Scoring Legend */}
        <Card className="bg-gray-50">
          <CardContent className="p-4">
            <p className="text-sm font-medium mb-2">Negative Scoring Legend (Lower = Better)</p>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-600"></div>
                <span>0-10% Excellent</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <span>11-25% Good</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                <span>26-40% Needs Attention</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-600"></div>
                <span>&gt;40% Critical</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              <strong>ND</strong> = % Not Done (task never completed - most serious) | 
              <strong>NDOT</strong> = % Not Done On Time (task done late or not done)
            </p>
          </CardContent>
        </Card>

        {/* No MIS Data Message */}
        {!loadingMIS && !misScoreData && selectedWeek && (
          <Card className="border-2 border-gray-200">
            <CardContent className="p-8 text-center">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium text-gray-600">No productivity data for this week</p>
              <p className="text-sm text-muted-foreground mt-2">
                You may not have any tasks assigned or completed in the selected week.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Progress Bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-600" />
                <span className="font-medium">Overall Progress</span>
              </div>
              <span className="text-sm font-semibold text-blue-600">{stats.weeklyProgress}%</span>
            </div>
            <Progress value={stats.weeklyProgress} className="h-3" />
            <p className="text-xs text-muted-foreground mt-2">
              {stats.completedTasks} of {stats.completedTasks + stats.pendingTasks} tasks completed
            </p>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 gap-6">
          {/* Action Required - Overdue + Today's Tasks */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <ListTodo className="w-5 h-5 text-orange-500" />
                Action Required
              </CardTitle>
              <CardDescription>Overdue and today&apos;s tasks that need your attention</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : (overdueTasks.length === 0 && todaysTasks.length === 0) ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <p className="text-muted-foreground">All caught up! No pending or overdue tasks.</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                  {/* Overdue Tasks Section */}
                  {overdueTasks.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-red-600 font-semibold sticky top-0 bg-white py-1">
                        <AlertCircle className="w-4 h-4" />
                        <span>OVERDUE ({overdueTasks.length})</span>
                      </div>
                      {overdueTasks.map((task, idx) => {
                        const dueDate = new Date(task.due_at || task.due_date || task.deadline || task.next_due);
                        const today = new Date();
                        const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
                        const isCATask = task.source_type === 'complaint_ca';
                        const isPATask = task.source_type === 'complaint_pa';
                        const isComplaintLinked = isCATask || isPATask;
                        const isSWSTask = task.type === 'sws';
                        const behavior = getTaskCompletionBehavior(task);
                        const isAutoComplete = !behavior.canManuallyComplete;
                        return (
                          <div key={`overdue-${idx}`} className={`flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors ${isComplaintLinked ? 'border-l-4 border-l-orange-500' : ''} ${isSWSTask ? 'border-l-4 border-l-purple-500' : ''}`}>
                            <div className="w-2 h-2 rounded-full mt-2 bg-red-500" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{task.title || task.task_name}</p>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <Badge variant="outline" className="text-xs">
                                  {task.type === 'delegation' ? 'Delegation' : task.type === 'sws' ? 'SWS' : 'Checklist'}
                                </Badge>
                                {isComplaintLinked && (
                                  <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-300">
                                    {isCATask ? 'CA' : 'PA'}
                                  </Badge>
                                )}
                                <span className="text-xs text-red-600 font-medium">
                                  {daysOverdue} day{daysOverdue !== 1 ? 's' : ''} overdue
                                </span>
                              </div>
                            </div>
                            {behavior.navigationTarget ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="shrink-0 text-blue-600 border-blue-600 hover:bg-blue-50"
                                onClick={() => navigate(behavior.navigationTarget.path)}
                                data-testid={`nav-overdue-${task.id}`}
                              >
                                {behavior.navigationTarget.label}
                              </Button>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="shrink-0 text-green-600 border-green-600 hover:bg-green-50"
                                onClick={() => markTaskDone(task)}
                                data-testid={`mark-done-overdue-${task.id}`}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Done
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Today's Tasks Section */}
                  {todaysTasks.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-blue-600 font-semibold sticky top-0 bg-white py-1">
                        <Calendar className="w-4 h-4" />
                        <span>DUE TODAY ({todaysTasks.length})</span>
                      </div>
                      {todaysTasks.map((task, idx) => {
                        const isCATask = task.source_type === 'complaint_ca';
                        const isPATask = task.source_type === 'complaint_pa';
                        const isComplaintLinked = isCATask || isPATask;
                        const isSWSTask = task.type === 'sws';
                        const behavior = getTaskCompletionBehavior(task);
                        const isAutoComplete = !behavior.canManuallyComplete;
                        return (
                          <div key={`today-${idx}`} className={`flex items-start gap-3 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors ${isComplaintLinked ? 'border-l-4 border-l-orange-500' : ''} ${isSWSTask ? 'border-l-4 border-l-purple-500' : ''}`}>
                            <div className={`w-2 h-2 rounded-full mt-2 ${
                              task.priority === 'High' || task.priority === 'critical' ? 'bg-red-500' : 
                              task.priority === 'Medium' ? 'bg-yellow-500' : 'bg-green-500'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{task.title || task.task_name}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs">
                                  {task.type === 'delegation' ? 'Delegation' : task.type === 'sws' ? 'SWS' : 'Checklist'}
                                </Badge>
                                {isComplaintLinked && (
                                  <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-300">
                                    {isCATask ? 'CA' : 'PA'}
                                  </Badge>
                                )}
                                {task.priority && (
                                  <Badge className={`text-xs ${getPriorityColor(task.priority)}`}>
                                    {task.priority}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            {behavior.navigationTarget ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="shrink-0 text-blue-600 border-blue-600 hover:bg-blue-50"
                                onClick={() => navigate(behavior.navigationTarget.path)}
                                data-testid={`nav-today-${task.id}`}
                              >
                                {behavior.navigationTarget.label}
                              </Button>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="shrink-0 text-green-600 border-green-600 hover:bg-green-50"
                                onClick={() => markTaskDone(task)}
                                data-testid={`mark-done-today-${task.id}`}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Done
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  <Button 
                    variant="outline" 
                    className="w-full mt-2"
                    onClick={() => navigate('/my-tasks')}
                  >
                    View All Tasks
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* This Week's Completions with Undo (same day only) */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Award className="w-5 h-5 text-green-500" />
                This Week&apos;s Completions
              </CardTitle>
              <CardDescription>Tasks completed this week (Undo available for today&apos;s tasks only)</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : recentActivity.length === 0 ? (
                <div className="text-center py-8">
                  <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">Complete tasks to see them here!</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                  {recentActivity.map((task, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                      <CheckSquare className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-green-900">{task.title || task.task_name}</p>
                        <p className="text-xs text-green-700">
                          Completed {task.completed_at ? new Date(task.completed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'recently'}
                        </p>
                      </div>
                      {isCompletedToday(task) && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="shrink-0 text-orange-600 border-orange-600 hover:bg-orange-50"
                          onClick={() => undoTaskCompletion(task)}
                          data-testid={`undo-task-${task.id}`}
                        >
                          <Undo2 className="w-4 h-4 mr-1" />
                          Undo
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Optional Widgets Section - Based on User's allowed_reports */}
        {(hasReportPermission('pending_gate_entries') || 
          hasReportPermission('pending_jc_issues') || 
          hasReportPermission('pending_indents') || 
          hasReportPermission('pending_pos')) && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Additional Reports
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Pending Gate Entries Widget */}
              {hasReportPermission('pending_gate_entries') && (
                <Card className="border-blue-200 bg-blue-50/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Package className="w-4 h-4 text-blue-600" />
                      Pending Gate Entries
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {optionalWidgets.pending_gate_entries.loading ? (
                      <div className="text-center py-4 text-muted-foreground text-sm">Loading...</div>
                    ) : optionalWidgets.pending_gate_entries.error ? (
                      <div className="text-center py-4 text-red-500 text-sm">{optionalWidgets.pending_gate_entries.error}</div>
                    ) : optionalWidgets.pending_gate_entries.data ? (
                      <div className="space-y-2">
                        <div className="text-3xl font-bold text-blue-600">
                          {optionalWidgets.pending_gate_entries.data.total_pending || 0}
                        </div>
                        <p className="text-xs text-muted-foreground">Entries awaiting processing</p>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="w-full mt-2"
                          onClick={() => navigate('/gate-entry')}
                        >
                          View All
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-muted-foreground text-sm">No data</div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Pending JC Issues Widget */}
              {hasReportPermission('pending_jc_issues') && (
                <Card className="border-orange-200 bg-orange-50/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Wrench className="w-4 h-4 text-orange-600" />
                      Pending JC Issues
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {optionalWidgets.pending_jc_issues.loading ? (
                      <div className="text-center py-4 text-muted-foreground text-sm">Loading...</div>
                    ) : optionalWidgets.pending_jc_issues.error ? (
                      <div className="text-center py-4 text-red-500 text-sm">{optionalWidgets.pending_jc_issues.error}</div>
                    ) : optionalWidgets.pending_jc_issues.data ? (
                      <div className="space-y-2">
                        <div className="text-3xl font-bold text-orange-600">
                          {optionalWidgets.pending_jc_issues.data.total_pending || 0}
                        </div>
                        <p className="text-xs text-muted-foreground">Issues awaiting action</p>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="w-full mt-2"
                          onClick={() => navigate('/store-issuing')}
                        >
                          View All
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-muted-foreground text-sm">No data</div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Pending Indents Widget */}
              {hasReportPermission('pending_indents') && (
                <Card className="border-purple-200 bg-purple-50/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText className="w-4 h-4 text-purple-600" />
                      Pending Indents
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {optionalWidgets.pending_indents.loading ? (
                      <div className="text-center py-4 text-muted-foreground text-sm">Loading...</div>
                    ) : optionalWidgets.pending_indents.error ? (
                      <div className="text-center py-4 text-red-500 text-sm">{optionalWidgets.pending_indents.error}</div>
                    ) : optionalWidgets.pending_indents.data ? (
                      <div className="space-y-2">
                        <div className="text-3xl font-bold text-purple-600">
                          {optionalWidgets.pending_indents.data.total_pending || 0}
                        </div>
                        <p className="text-xs text-muted-foreground">Indents awaiting approval</p>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="w-full mt-2"
                          onClick={() => navigate('/indent-register')}
                        >
                          View All
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-muted-foreground text-sm">No data</div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Pending POs Widget */}
              {hasReportPermission('pending_pos') && (
                <Card className="border-green-200 bg-green-50/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4 text-green-600" />
                      Pending POs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {optionalWidgets.pending_pos.loading ? (
                      <div className="text-center py-4 text-muted-foreground text-sm">Loading...</div>
                    ) : optionalWidgets.pending_pos.error ? (
                      <div className="text-center py-4 text-red-500 text-sm">{optionalWidgets.pending_pos.error}</div>
                    ) : optionalWidgets.pending_pos.data ? (
                      <div className="space-y-2">
                        <div className="text-3xl font-bold text-green-600">
                          {optionalWidgets.pending_pos.data.total_pending || 0}
                        </div>
                        <p className="text-xs text-muted-foreground">POs awaiting approval</p>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="w-full mt-2"
                          onClick={() => navigate('/purchase-orders')}
                        >
                          View All
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-muted-foreground text-sm">No data</div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Incoming Transfers Widget - Based on Team Mapping (store_incharge) */}
        {optionalWidgets.incoming_transfers.data?.total_count > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Incoming Transfers
              <Badge variant="secondary" className="ml-2">
                {optionalWidgets.incoming_transfers.data.total_count}
              </Badge>
            </h2>
            <Card className="border-purple-200 bg-purple-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Truck className="w-4 h-4 text-purple-600" />
                  IUTs Awaiting Receipt
                </CardTitle>
                <CardDescription className="text-xs">
                  {optionalWidgets.incoming_transfers.data.user_locations?.length > 0 && (
                    <span>For: {optionalWidgets.incoming_transfers.data.user_locations.join(', ')}</span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {optionalWidgets.incoming_transfers.data.transfers.slice(0, 5).map((transfer) => (
                    <div 
                      key={transfer.id} 
                      className="flex items-center justify-between p-2 bg-white rounded-lg border hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate('/store/inter-unit-transfers')}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium text-purple-600">
                            {transfer.transfer_number}
                          </span>
                          {transfer.vehicle_no && (
                            <Badge variant="outline" className="text-xs">
                              {transfer.vehicle_no}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          From: {transfer.from_unit_name} • {transfer.item_name} ({transfer.quantity} {transfer.unit})
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  ))}
                  {optionalWidgets.incoming_transfers.data.total_count > 5 && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="w-full mt-2"
                      onClick={() => navigate('/store/inter-unit-transfers')}
                    >
                      View All ({optionalWidgets.incoming_transfers.data.total_count})
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Task Details Dialog */}
      <Dialog open={taskDetailsOpen} onOpenChange={setTaskDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {taskDetailsCategory === 'delegation' && <ListTodo className="w-5 h-5 text-purple-600" />}
              {taskDetailsCategory === 'checklist' && <ClipboardList className="w-5 h-5 text-green-600" />}
              {taskDetailsCategory === 'sws' && <GitBranch className="w-5 h-5 text-orange-600" />}
              {taskDetailsCategory?.charAt(0).toUpperCase() + taskDetailsCategory?.slice(1)} Tasks
              {taskDetailsData?.week && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  - Week {taskDetailsData.week.number}, {taskDetailsData.week.year}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Your task details for the selected week
            </DialogDescription>
          </DialogHeader>

          {taskDetailsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : taskDetailsData ? (
            <div className="flex-1 overflow-hidden flex flex-col gap-4">
              {/* Summary and Filters */}
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex gap-2">
                  <Badge 
                    variant={taskDetailsFilter === 'all' ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setTaskDetailsFilter('all')}
                  >
                    All ({taskDetailsData.tasks?.length || 0})
                  </Badge>
                  <Badge 
                    variant={taskDetailsFilter === 'done' ? 'default' : 'outline'}
                    className="cursor-pointer bg-green-100 text-green-800 hover:bg-green-200"
                    onClick={() => setTaskDetailsFilter('done')}
                  >
                    Done ({taskDetailsData.summary?.done || 0})
                  </Badge>
                  <Badge 
                    variant={taskDetailsFilter === 'ndot' ? 'default' : 'outline'}
                    className="cursor-pointer bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                    onClick={() => setTaskDetailsFilter('ndot')}
                  >
                    Late ({taskDetailsData.summary?.ndot || 0})
                  </Badge>
                  <Badge 
                    variant={taskDetailsFilter === 'nd' ? 'default' : 'outline'}
                    className="cursor-pointer bg-red-100 text-red-800 hover:bg-red-200"
                    onClick={() => setTaskDetailsFilter('nd')}
                  >
                    Pending ({taskDetailsData.summary?.nd || 0})
                  </Badge>
                </div>
                
                {/* Task counts info */}
                <div className="text-xs text-muted-foreground">
                  Week: {taskDetailsData.task_counts?.week_tasks || 0} | 
                  Rolled: {taskDetailsData.task_counts?.rolled_tasks || 0}
                </div>
              </div>

              {/* Task Table */}
              <div className="flex-1 overflow-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      {taskDetailsCategory === 'sws' ? (
                        <>
                          <TableHead>Step Name</TableHead>
                          <TableHead>Workflow</TableHead>
                          <TableHead className="text-center">Due At</TableHead>
                          <TableHead className="text-center">Done At</TableHead>
                        </>
                      ) : taskDetailsCategory === 'checklist' ? (
                        <>
                          <TableHead>Checklist</TableHead>
                          <TableHead className="text-center">Due Date</TableHead>
                          <TableHead className="text-center">Done On</TableHead>
                        </>
                      ) : (
                        <>
                          <TableHead>Task</TableHead>
                          <TableHead>Assigned By</TableHead>
                          <TableHead className="text-center">Due Date</TableHead>
                          <TableHead className="text-center">Done On</TableHead>
                        </>
                      )}
                      <TableHead className="text-center">R/O</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTaskDetails.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No tasks found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTaskDetails.map((task) => (
                        <TableRow key={task.id} className="hover:bg-muted/30">
                          {taskDetailsCategory === 'sws' ? (
                            <>
                              <TableCell className="max-w-[200px] truncate" title={task.step_name}>
                                {task.step_name}
                              </TableCell>
                              <TableCell className="max-w-[150px] truncate" title={task.workflow_name}>
                                {task.workflow_name}
                              </TableCell>
                              <TableCell className="text-center">{task.due_at}</TableCell>
                              <TableCell className="text-center">{task.done_at || '-'}</TableCell>
                            </>
                          ) : taskDetailsCategory === 'checklist' ? (
                            <>
                              <TableCell className="max-w-[250px] truncate" title={task.title}>
                                {task.title}
                              </TableCell>
                              <TableCell className="text-center">{task.occurrence_date}</TableCell>
                              <TableCell className="text-center">{task.done_on || '-'}</TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell className="max-w-[200px] truncate" title={task.title}>
                                {task.title}
                              </TableCell>
                              <TableCell className="max-w-[120px] truncate" title={task.assigned_by}>
                                {task.assigned_by}
                              </TableCell>
                              <TableCell className="text-center">{task.due_date}</TableCell>
                              <TableCell className="text-center">{task.done_on || '-'}</TableCell>
                            </>
                          )}
                          <TableCell className="text-center">
                            {task.is_rolled ? (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                                <Undo2 className="w-3 h-3 mr-1" />
                                Yes
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {task.status === 'done' && (
                              <Badge className="bg-green-100 text-green-700 border-green-200">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Done
                              </Badge>
                            )}
                            {task.status === 'ndot' && (
                              <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                                <Clock className="w-3 h-3 mr-1" />
                                Late
                              </Badge>
                            )}
                            {task.status === 'nd' && (
                              <Badge className="bg-red-100 text-red-700 border-red-200">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
